// src/slices/ticketRecallAppraisal/thunk.ts
// Async thunks cho màn "Duyệt thu hồi vé ế" (mục 22/23, REQUIRE_FIXBUGS.md).
// Bám đúng TicketRecallController (query PascalCase, approve/reject trả về `long`).

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getTicketRecallsApi,
  getTicketRecallByIdApi,
  approveTicketRecallApi,
  rejectTicketRecallApi,
} from 'helpers/fakebackend_helper';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_recall_appraisal_messages');

interface FetchTicketRecallsForAppraisalParams {
  status?: number | string;
  // Lọc gộp nhiều trạng thái cùng lúc (VD "Tất cả trạng thái" của màn thẩm định = Chờ thẩm định +
  // Đã duyệt + Từ chối trong 1 request, để tổng số/phân trang đúng) — ưu tiên hơn `status` đơn lẻ.
  statuses?: number[];
  planId?: number | string;
  agentId?: number | string;
  pageIndex?: number;
  pageSize?: number;
}

const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

export const fetchAppraisalRecalls = createAsyncThunk(
  'TicketRecallAppraisal/fetchList',
  async (params: FetchTicketRecallsForAppraisalParams, { rejectWithValue }) => {
    try {
      const response = await getTicketRecallsApi(
        cleanParams({
          Status: params.statuses?.length ? undefined : params.status,
          Statuses: params.statuses?.length ? params.statuses : undefined,
          PlanId: params.planId,
          AgentId: params.agentId,
          PageIndex: params.pageIndex,
          PageSize: params.pageSize,
        }),
      );
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_danh_sach_phieu_message'));
    }
  },
);

export const fetchAppraisalRecallById = createAsyncThunk(
  'TicketRecallAppraisal/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getTicketRecallByIdApi(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_chi_tiet_phieu_message'));
    }
  },
);

/**
 * Duyệt phiếu thu hồi (Confirmed -> Approved). Mục 8.d, README_UPDATE.md — endpoint giờ trả về
 * ApproveTicketRecallResultDto { id, reconciledLineCount, reconciledQuantity } (trước đây chỉ
 * `long`), kèm tóm tắt đối soát đã chạy khi Duyệt — nơi gọi vẫn phải tự refetch danh sách để thấy
 * trạng thái mới (thunk này không tự cập nhật list).
 */
export const approveTicketRecall = createAsyncThunk(
  'TicketRecallAppraisal/approve',
  async ({ id, comment }: { id: number; comment?: string }, { rejectWithValue }) => {
    try {
      const response = await approveTicketRecallApi(id, comment);
      return (response as any)?.data ?? response ?? id;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_duyet_phieu_thu_hoi_message'));
    }
  },
);

/** Từ chối phiếu thu hồi (Confirmed -> Rejected), bắt buộc lý do. */
export const rejectTicketRecall = createAsyncThunk(
  'TicketRecallAppraisal/reject',
  async ({ id, comment }: { id: number; comment: string }, { rejectWithValue }) => {
    try {
      const response = await rejectTicketRecallApi(id, comment);
      return (response as any)?.data ?? response ?? id;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tu_choi_phieu_thu_message'));
    }
  },
);
